import React from 'react';
import { Button } from '../components/Button';
import { VideoIcon, ClipboardListIcon, HeartPulseIcon, StarIcon, CheckCircle2Icon, XCircleIcon, ImageIcon, QuoteIcon } from 'lucide-react';
interface HomePageProps {
  setPage: (page: 'home' | 'about' | 'contact') => void;
}
export function HomePage({
  setPage
}: HomePageProps) {
  const scrollToSteps = () => {
    const stepsSection = document.getElementById('steps-section');
    if (stepsSection) {
      stepsSection.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  return <div className="flex flex-col w-full" data-id="element-195">
      {/* Hero Section - Flipped layout with eyebrow */}
      <section className="relative bg-cream py-16 md:py-24 overflow-hidden" data-id="element-196">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-197">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-198">
            {/* Image Left */}
            <div className="order-2 lg:order-1 relative h-[400px] md:h-[600px] rounded-r-[4rem] rounded-l-2xl overflow-hidden bg-stone-200 flex flex-col items-center justify-center border-2 border-dashed border-stone-300 shadow-inner" data-id="element-199">
              <ImageIcon className="h-16 w-16 text-stone-400 mb-4" data-id="element-200" />
              <p className="text-stone-500 font-medium text-center px-6" data-id="element-201">
                [Placeholder: Lifestyle image — active, confident man enjoying
                daily life]
              </p>
            </div>

            {/* Text Right */}
            <div className="order-1 lg:order-2 max-w-2xl lg:pl-8" data-id="element-202">
              <span className="block text-accent font-bold tracking-widest uppercase text-sm mb-4" data-id="element-203">
                Telehealth TRT in [STATE]
              </span>
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-primary leading-tight mb-6" data-id="element-204">
                Feel Like Yourself Again
              </h1>
              <p className="text-lg md:text-xl text-charcoal mb-8 leading-relaxed" data-id="element-205">
                [TAGLINE]. Discover how our personalized telehealth TRT programs
                are helping men in [CITY] and across [STATE] get their energy,
                focus, and confidence back—all from the comfort of home.
              </p>
              <div className="flex flex-col sm:flex-row gap-4" data-id="element-206">
                <Button onClick={() => setPage('contact')} variant="primary" className="text-lg px-8 py-4" data-id="element-207">
                  Schedule your tele-health appointment
                </Button>
                <Button onClick={scrollToSteps} variant="outline" className="text-lg px-8 py-4 bg-transparent" data-id="element-208">
                  See how the process works
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Before & After Section - Staggered overlapping layout */}
      <section className="py-24 bg-white overflow-hidden" data-id="element-209">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-210">
          <div className="text-center mb-16" data-id="element-211">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary mb-4" data-id="element-212">
              A Day in Your Life — Before & After
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto" data-id="element-213">
              Low testosterone affects every part of your day. See the
              difference personalized care can make.
            </p>
          </div>

          <div className="relative max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-center pt-8 pb-16" data-id="element-214">
            {/* Before Card - Higher, narrower */}
            <div className="w-full md:w-[45%] bg-stone-50 rounded-2xl p-8 md:p-10 border border-stone-200 z-10 md:-translate-y-8 shadow-md" data-id="element-215">
              <h3 className="font-heading text-2xl font-semibold text-stone-600 mb-6 flex items-center" data-id="element-216">
                <XCircleIcon className="h-6 w-6 mr-3 text-stone-400" data-id="element-217" />
                Before TRT
              </h3>
              <ul className="space-y-5" data-id="element-218">
                {['Dragging through the day, relying on caffeine', 'Low drive and motivation for work or hobbies', 'Struggling in the gym, losing muscle mass', 'Brain fog, irritability, and poor sleep'].map((item, i) => <li key={i} className="flex items-start text-stone-600" data-id="element-219">
                    <span className="h-2 w-2 rounded-full bg-stone-300 mt-2 mr-3 flex-shrink-0" data-id="element-220"></span>
                    <span className="text-lg" data-id="element-221">{item}</span>
                  </li>)}
              </ul>
            </div>

            {/* After Card - Lower, wider, overlapping */}
            <div className="w-full md:w-[60%] bg-white rounded-2xl p-8 md:p-12 shadow-2xl border-t-4 border-accent relative z-20 mt-8 md:mt-0 md:-ml-12 md:translate-y-8" data-id="element-222">
              <div className="absolute top-0 right-0 bg-accent text-primary text-xs font-bold px-4 py-2 rounded-bl-lg" data-id="element-223">
                THE GOAL
              </div>
              <h3 className="font-heading text-3xl font-semibold text-primary mb-8 flex items-center" data-id="element-224">
                <CheckCircle2Icon className="h-8 w-8 mr-4 text-accent" data-id="element-225" />
                After care with [CLINIC NAME]
              </h3>
              <ul className="space-y-6" data-id="element-226">
                {['Sustained energy all day, from morning to night', 'Renewed motivation and drive in all areas of life', 'Better results in the gym and faster recovery', 'Clearer focus, better mood, and feeling like yourself again'].map((item, i) => <li key={i} className="flex items-start text-charcoal" data-id="element-227">
                    <span className="h-2.5 w-2.5 rounded-full bg-accent mt-2 mr-4 flex-shrink-0" data-id="element-228"></span>
                    <span className="text-xl font-medium" data-id="element-229">{item}</span>
                  </li>)}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* 3 Steps Section - Horizontal connected path */}
      <section id="steps-section" className="py-24 bg-cream" data-id="element-230">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-231">
          <div className="text-center mb-20" data-id="element-232">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary mb-4" data-id="element-233">
              Your Path in 3 Simple Steps
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto" data-id="element-234">
              We've made the process straightforward and entirely remote.
            </p>
          </div>

          <div className="relative" data-id="element-235">
            {/* Connecting line (desktop) */}
            <div className="hidden md:block absolute top-12 left-[16.66%] right-[16.66%] h-0.5 bg-stone-300 border-t-2 border-dashed border-stone-300 z-0" data-id="element-236"></div>

            {/* Connecting line (mobile) */}
            <div className="md:hidden absolute top-12 bottom-12 left-12 w-0.5 bg-stone-300 border-l-2 border-dashed border-stone-300 z-0" data-id="element-237"></div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-8 relative z-10" data-id="element-238">
              {/* Step 1 */}
              <div className="flex flex-row md:flex-col items-start md:items-center text-left md:text-center group" data-id="element-239">
                <div className="w-24 h-24 flex-shrink-0 mx-0 md:mx-auto bg-white rounded-full flex items-center justify-center mb-0 md:mb-8 shadow-lg border-4 border-cream relative z-10 group-hover:border-accent transition-colors duration-300" data-id="element-240">
                  <VideoIcon className="h-10 w-10 text-primary" data-id="element-241" />
                  <div className="absolute -top-2 -right-2 w-8 h-8 bg-accent text-primary font-bold rounded-full flex items-center justify-center text-sm shadow-sm" data-id="element-242">
                    1
                  </div>
                </div>
                <div className="ml-6 md:ml-0 mt-2 md:mt-0" data-id="element-243">
                  <h3 className="font-heading text-xl font-bold text-primary mb-3" data-id="element-244">
                    Quick Telehealth Consultation
                  </h3>
                  <p className="text-gray-600" data-id="element-245">
                    Connect with a provider from the comfort of home. No waiting
                    rooms, no hassle.
                  </p>
                </div>
              </div>

              {/* Step 2 */}
              <div className="flex flex-row md:flex-col items-start md:items-center text-left md:text-center group" data-id="element-246">
                <div className="w-24 h-24 flex-shrink-0 mx-0 md:mx-auto bg-white rounded-full flex items-center justify-center mb-0 md:mb-8 shadow-lg border-4 border-cream relative z-10 group-hover:border-accent transition-colors duration-300" data-id="element-247">
                  <ClipboardListIcon className="h-10 w-10 text-primary" data-id="element-248" />
                  <div className="absolute -top-2 -right-2 w-8 h-8 bg-accent text-primary font-bold rounded-full flex items-center justify-center text-sm shadow-sm" data-id="element-249">
                    2
                  </div>
                </div>
                <div className="ml-6 md:ml-0 mt-2 md:mt-0" data-id="element-250">
                  <h3 className="font-heading text-xl font-bold text-primary mb-3" data-id="element-251">
                    Personalized Treatment Plan
                  </h3>
                  <p className="text-gray-600" data-id="element-252">
                    Get a plan tailored to your body, your goals, and your
                    lifestyle based on comprehensive lab work.
                  </p>
                </div>
              </div>

              {/* Step 3 */}
              <div className="flex flex-row md:flex-col items-start md:items-center text-left md:text-center group" data-id="element-253">
                <div className="w-24 h-24 flex-shrink-0 mx-0 md:mx-auto bg-white rounded-full flex items-center justify-center mb-0 md:mb-8 shadow-lg border-4 border-cream relative z-10 group-hover:border-accent transition-colors duration-300" data-id="element-254">
                  <HeartPulseIcon className="h-10 w-10 text-primary" data-id="element-255" />
                  <div className="absolute -top-2 -right-2 w-8 h-8 bg-accent text-primary font-bold rounded-full flex items-center justify-center text-sm shadow-sm" data-id="element-256">
                    3
                  </div>
                </div>
                <div className="ml-6 md:ml-0 mt-2 md:mt-0" data-id="element-257">
                  <h3 className="font-heading text-xl font-bold text-primary mb-3" data-id="element-258">
                    Ongoing Follow-Up & Adjustments
                  </h3>
                  <p className="text-gray-600" data-id="element-259">
                    We stay with you — regular check-ins to make sure you are
                    feeling your absolute best.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section - Featured layout */}
      <section className="py-24 bg-white" data-id="element-260">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-261">
          <div className="mb-16" data-id="element-262">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary mb-4" data-id="element-263">
              Stories & Experiences
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl" data-id="element-264">
              Hear from men who have taken the step to reclaim their vitality.
            </p>
          </div>

          <div className="flex flex-col lg:flex-row gap-8" data-id="element-265">
            {/* Featured Testimonial (Left, 60%) */}
            <div className="w-full lg:w-[60%] bg-primary rounded-2xl p-10 md:p-14 shadow-xl text-white relative overflow-hidden" data-id="element-266">
              <QuoteIcon className="absolute top-8 right-8 h-32 w-32 text-slate-700/30 rotate-180" data-id="element-267" />
              <div className="relative z-10" data-id="element-268">
                <div className="flex text-accent mb-8" data-id="element-269">
                  {[...Array(5)].map((_, j) => <StarIcon key={j} className="h-6 w-6 fill-current" data-id="element-270" />)}
                </div>
                <p className="font-heading text-2xl md:text-3xl leading-relaxed italic mb-8" data-id="element-271">
                  "I didn't realize how much I was just 'getting by' until I
                  started my plan. Within weeks, I had the energy to play with
                  my kids after work again. It's been life-changing."
                </p>
                <div className="flex items-center" data-id="element-272">
                  <div className="h-12 w-12 rounded-full bg-slate-800 flex items-center justify-center font-bold text-accent mr-4" data-id="element-273">
                    MR
                  </div>
                  <div className="font-bold text-lg tracking-wide" data-id="element-274">— M.R.</div>
                </div>
              </div>
            </div>

            {/* Smaller Testimonials (Right, 40%) */}
            <div className="w-full lg:w-[40%] flex flex-col gap-8" data-id="element-275">
              <div className="bg-cream rounded-2xl p-8 shadow-sm flex-1" data-id="element-276">
                <div className="flex text-accent mb-4" data-id="element-277">
                  {[...Array(5)].map((_, j) => <StarIcon key={j} className="h-4 w-4 fill-current" data-id="element-278" />)}
                </div>
                <p className="text-charcoal italic mb-6" data-id="element-279">
                  "The telehealth aspect makes it so easy. I run a business and
                  don't have time for doctor's waiting rooms. The team is
                  responsive, and I feel 10 years younger."
                </p>
                <div className="font-bold text-primary" data-id="element-280">— J.T.</div>
              </div>

              <div className="bg-cream rounded-2xl p-8 shadow-sm flex-1" data-id="element-281">
                <div className="flex text-accent mb-4" data-id="element-282">
                  {[...Array(5)].map((_, j) => <StarIcon key={j} className="h-4 w-4 fill-current" data-id="element-283" />)}
                </div>
                <p className="text-charcoal italic mb-6" data-id="element-284">
                  "Finally, a clinic that listens. They didn't just look at a
                  number on a chart; they asked how I was feeling. My workouts
                  are great again, and my brain fog is gone."
                </p>
                <div className="font-bold text-primary" data-id="element-285">— D.K.</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Band - Left aligned with decorative shape */}
      <section className="bg-primary py-20 relative overflow-hidden" data-id="element-286">
        {/* Decorative Shape */}
        <div className="absolute -right-20 top-1/2 -translate-y-1/2 w-96 h-96 bg-accent/10 rounded-full blur-3xl pointer-events-none" data-id="element-287"></div>
        <QuoteIcon className="absolute right-10 top-1/2 -translate-y-1/2 h-64 w-64 text-slate-700/20 rotate-180 pointer-events-none hidden md:block" data-id="element-288" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-289">
          <div className="max-w-2xl" data-id="element-290">
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-white mb-8 leading-tight" data-id="element-291">
              Ready to Start Your Journey?
            </h2>
            <Button onClick={() => setPage('contact')} variant="primary" className="text-lg px-10 py-4 shadow-lg" data-id="element-292">
              Schedule your tele-health appointment
            </Button>
          </div>
        </div>
      </section>
    </div>;
}